import { useEffect, useState, useCallback, useRef } from 'react';
import { Instagram, Loader2, Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { autofillFromUploadedVideo } from '@/lib/publishVideoAutofill';
import { openInstagramOAuthWindow, subscribeInstagramOAuthResult } from '@/lib/instagramOAuthPopup';
import {
  applyInstagramPublishDraft,
  buildInstagramPublishDraft,
  clearInstagramPublishDraft,
  loadInstagramPublishDraft,
  saveInstagramPublishDraft,
} from '@/lib/instagramPublishDraft';
import { PublishThumbnailPicker } from '@/components/social/PublishThumbnailPicker';

import { TwinneeLogo } from '@/components/TwinneeLogo';
/**
 * Instagram OAuth + Reels publish (POST /api/social-publish/instagram/upload).
 */
export function InstagramPublishDialog({
  open,
  onOpenChange,
  oauthReturnPath,
  defaultVideoUrl = '',
  defaultTitle = '',
  scriptContext = '',
  tone = '',
  visualStyle = '',
  narrationProjectId = null,
  storyId = null,
  onPublishStart,
  onPublished,
  onPublishFailed,
}) {
  const [status, setStatus] = useState({ connected: false, username: null });
  const [statusLoading, setStatusLoading] = useState(false);
  const [beginBusy, setBeginBusy] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [aiSuggestBusy, setAiSuggestBusy] = useState(false);
  const [aiSuggestNote, setAiSuggestNote] = useState('');

  const [videoUrl, setVideoUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [coverFrameUrls, setCoverFrameUrls] = useState([]);
  const [shareToFeed, setShareToFeed] = useState(true);
  const [metadataOptimizeLang, setMetadataOptimizeLang] = useState('en');
  const [instagramReelsMode, setInstagramReelsMode] = useState(true);
  const [oauthWaiting, setOauthWaiting] = useState(false);
  const oauthPopupRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/social-publish/instagram/status');
      setStatus({
        connected: Boolean(data.connected),
        username: data.username || null,
        page_name: data.page_name || null,
      });
    } catch {
      setStatus({ connected: false, username: null });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    setStatusLoading(true);
    loadStatus();
    return undefined;
  }, [open, loadStatus]);

  useEffect(() => {
    if (!open) return;
    const draft = loadInstagramPublishDraft();
    if (draft) {
      applyInstagramPublishDraft(draft, {
        setVideoUrl,
        setCaption,
        setCoverUrl,
        setShareToFeed,
        setMetadataOptimizeLang,
        setInstagramReelsMode,
      });
      return;
    }
    setVideoUrl(defaultVideoUrl || '');
    if (defaultTitle && !caption) {
      setCaption(defaultTitle.trim());
    }
  }, [open, defaultVideoUrl, defaultTitle]);

  const collectFormDraft = useCallback(
    () =>
      buildInstagramPublishDraft({
        videoUrl,
        caption,
        coverUrl,
        shareToFeed,
        metadataOptimizeLang,
        instagramReelsMode,
      }),
    [videoUrl, caption, coverUrl, shareToFeed, metadataOptimizeLang, instagramReelsMode],
  );

  const handleOAuthResult = useCallback(
    async ({ status: oauthStatus, message }) => {
      setOauthWaiting(false);
      setBeginBusy(false);
      oauthPopupRef.current = null;
      if (oauthStatus === 'connected') {
        toast.success('Instagram connected.');
        clearInstagramPublishDraft();
        await loadStatus();
        return;
      }
      const detail = message && message !== 'oauth_error' ? message : 'Instagram connection failed';
      toast.error(detail);
      await loadStatus();
    },
    [loadStatus],
  );

  useEffect(() => {
    if (!oauthWaiting) return undefined;
    return subscribeInstagramOAuthResult({
      popupRef: oauthPopupRef,
      onResult: (payload) => {
        handleOAuthResult(payload);
      },
      onPopupClosed: () => {
        setOauthWaiting(false);
        setBeginBusy(false);
        oauthPopupRef.current = null;
        loadStatus();
      },
    });
  }, [oauthWaiting, handleOAuthResult, loadStatus]);

  const handleConnectInstagram = async () => {
    setBeginBusy(true);
    let openedPopup = false;
    saveInstagramPublishDraft(collectFormDraft());
    try {
      const path = oauthReturnPath || `${window.location.pathname}${window.location.search}`;
      const { data } = await api.post('/social-publish/instagram/oauth/begin', {
        return_path: path.startsWith('/') ? path : `/${path}`,
        oauth_mode: 'popup',
        frontend_origin: window.location.origin,
      });
      if (data.authorization_url) {
        const popup = openInstagramOAuthWindow(data.authorization_url);
        if (!popup) {
          toast.error('Allow pop-ups for this site to connect Instagram, or try again.');
          setBeginBusy(false);
          return;
        }
        oauthPopupRef.current = popup;
        setOauthWaiting(true);
        openedPopup = true;
        toast.info('Complete Meta sign-in in the popup. Your form is saved here.');
        return;
      }
      toast.error('Could not start Instagram login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OAuth not configured or failed');
    } finally {
      if (!openedPopup) setBeginBusy(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnectBusy(true);
    try {
      await api.delete('/social-publish/instagram/disconnect');
      toast.success('Instagram disconnected');
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disconnect failed');
    } finally {
      setDisconnectBusy(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) {
      toast.error('Video URL is required');
      return;
    }
    if (!caption.trim()) {
      toast.error('Caption is required');
      return;
    }

    setUploadBusy(true);
    onPublishStart?.();
    try {
      const { data } = await api.post('/social-publish/instagram/upload', {
        video_url: videoUrl.trim(),
        caption: caption.trim(),
        share_to_feed: shareToFeed,
        cover_url: coverUrl.trim() || null,
        narration_project_id: narrationProjectId || null,
        story_id: storyId || null,
      });
      const link = data.permalink || (data.media_id ? `https://www.instagram.com/` : '');
      toast.success(link ? `Published: ${link}` : 'Published to Instagram');
      clearInstagramPublishDraft();
      if (Array.isArray(data.warnings) && data.warnings.length) {
        data.warnings.forEach((w) => toast.warning(w));
      }
      onPublished?.({ url: data.permalink || link, id: data.media_id || null });
      onOpenChange(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      const message = typeof d === 'string' ? d : err.message || 'Upload failed';
      toast.error(message);
      onPublishFailed?.(message);
    } finally {
      setUploadBusy(false);
    }
  };

  const handleAiSuggestCaption = async () => {
    setAiSuggestBusy(true);
    setAiSuggestNote('');
    try {
      const hint = (caption || defaultTitle || '').trim().split('\n')[0];
      const uploaded = videoUrl.trim();
      let data;
      if (uploaded) {
        setAiSuggestNote('Queued — transcribing the video…');
        data = await autofillFromUploadedVideo(
          {
            video_url: uploaded,
            platform: 'instagram',
            title_hint: '',
            script_text: '',
            tone: (tone || '').trim().slice(0, 200) || null,
            visual_style: (visualStyle || '').trim().slice(0, 200) || null,
            language: (metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
            optimize_for_reels: Boolean(instagramReelsMode),
          },
          { onProgress: (row) => setAiSuggestNote(row.message || 'Working…') },
        );
      } else {
        const response = await api.post('/social-publish/instagram/suggest-metadata', {
          title_hint: (hint || '').slice(0, 300),
          script_text: (scriptContext || '').trim().slice(0, 14000),
          tone: (tone || '').trim().slice(0, 200) || null,
          visual_style: (visualStyle || '').trim().slice(0, 200) || null,
          language: (metadataOptimizeLang || 'en').trim().slice(0, 12) || 'en',
          optimize_for_reels: Boolean(instagramReelsMode),
        });
        data = response.data;
      }
      if (data.caption) setCaption(String(data.caption).slice(0, 2200));
      const keywordLine = Array.isArray(data.keywords) && data.keywords.length
        ? `Keywords: ${data.keywords.slice(0, 6).join(', ')}.`
        : '';
      const tipLine = Array.isArray(data.seo_tips) && data.seo_tips.length
        ? data.seo_tips.slice(0, 2).join(' ')
        : '';
      setAiSuggestNote([keywordLine, tipLine].filter(Boolean).join(' '));
      toast.success(`AI caption applied${uploaded ? ' from the video transcript' : ''} — review before publishing.`);
    } catch (err) {
      if (err?.cancelled) return;
      const d = err.response?.data?.detail;
      setAiSuggestNote('');
      toast.error(typeof d === 'string' ? d : err.message || 'AI suggestion failed');
    } finally {
      setAiSuggestBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto flex flex-col gap-0 sm:max-w-xl"
        style={{ background: 'var(--bg-primary)', borderColor: 'var(--glass-border)' }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Instagram className="w-5 h-5 shrink-0" style={{ color: '#E1306C' }} />
            Publish to Instagram
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-secondary)' }}>
            Connect a Business or Creator Instagram account linked to a Facebook Page. The server publishes a Reel
            using your public MP4 URL (Instagram fetches the file). Vertical 9:16 exports work best.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 border-t border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
            Authentication
          </p>
          {statusLoading ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Checking…
            </div>
          ) : status.connected ? (
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                Connected: <strong>@{status.username || 'instagram'}</strong>
                {status.page_name ? (
                  <span className="text-[11px] block" style={{ color: 'var(--text-tertiary)' }}>
                    via {status.page_name}
                  </span>
                ) : null}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disconnectBusy}
                onClick={handleDisconnect}
                className="rounded-lg gap-1"
              >
                {disconnectBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                type="button"
                onClick={handleConnectInstagram}
                disabled={beginBusy || oauthWaiting}
                className="rounded-lg gap-2 text-white"
                style={{ background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}
              >
                {beginBusy || oauthWaiting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Connect Instagram (Meta)
              </Button>
              {oauthWaiting ? (
                <p className="text-[11px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
                  Sign in with Facebook/Meta in the popup. This dialog keeps your video URL and caption.
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="rounded-lg border px-3 py-2.5 space-y-3" style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Caption language</Label>
              <Select value={metadataOptimizeLang} onValueChange={setMetadataOptimizeLang}>
                <SelectTrigger className="rounded-lg text-sm h-9" data-testid="instagram-metadata-lang-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="hi">Hindi (हिंदी)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex flex-col gap-2 rounded-lg border px-3 py-2 cursor-pointer" style={{ borderColor: 'var(--glass-border)' }}>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Reels mode</span>
              <span className="flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                <input
                  type="checkbox"
                  checked={instagramReelsMode}
                  onChange={(e) => setInstagramReelsMode(e.target.checked)}
                  className="rounded border shrink-0"
                />
                Reels hooks + hashtags in AI caption
              </span>
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-lg gap-1.5"
            disabled={aiSuggestBusy}
            onClick={handleAiSuggestCaption}
            style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
            data-testid="instagram-ai-suggest-caption-btn"
          >
            {aiSuggestBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TwinneeLogo className="w-4 h-4" />}
            Autofill using AI
          </Button>
          {aiSuggestNote ? (
            <span className="text-[11px] leading-snug" style={{ color: 'var(--text-tertiary)' }}>
              {aiSuggestNote}
            </span>
          ) : null}
        </div>

        <form onSubmit={handleUpload} className="space-y-3 pt-2">
          <div className="space-y-1">
            <Label htmlFor="ig-v-url">Video URL *</Label>
            <Input
              id="ig-v-url"
              placeholder="HTTPS URL to MP4 (must be public; Instagram servers fetch it)"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className="rounded-lg text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ig-caption">Caption * (max 2200)</Label>
            <textarea
              id="ig-caption"
              rows={6}
              maxLength={2200}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="w-full rounded-lg border px-2 py-1.5 text-sm bg-transparent"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-primary)' }}
              placeholder="Caption + hashtags…"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={shareToFeed}
              onChange={(e) => setShareToFeed(e.target.checked)}
              className="rounded border"
            />
            Share Reel to profile grid (feed)
          </label>

          <PublishThumbnailPicker
            platform="instagram"
            videoUrl={videoUrl}
            title={(caption || defaultTitle || '').trim().split('\n')[0]}
            description={caption}
            scriptContext={scriptContext}
            tone={tone}
            visualStyle={visualStyle}
            thumbnailUrl={coverUrl}
            onThumbnailUrlChange={setCoverUrl}
            frameUrls={coverFrameUrls}
            onFrameUrlsChange={setCoverFrameUrls}
            label="Reel cover image (optional)"
            testIdPrefix="instagram-cover"
          />

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-lg">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploadBusy || !status.connected}
              className="rounded-lg gap-2"
              style={{ background: 'var(--primary)', color: 'var(--text-primary)' }}
              data-testid="instagram-publish-submit-btn"
            >
              {uploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Instagram className="w-4 h-4" />}
              Publish Reel
            </Button>
          </DialogFooter>
          {!status.connected && (
            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Connect Instagram above before publishing.</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
